import Breadcrumb, {Crumb} from "../../../components/common/Breadcrumbs";
import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import {createOptionListForSelectTag} from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {Routes as AppRoutes} from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import {
    useCreateCollectorLinkMutation,
    useDeleteCollectorLinkMutation,
    useGetCollectorLinkByIdQuery,
    useUpdateCollectorLinkMutation
} from "./collectorLinkApi";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import IconButton from "../../../components/common/form/IconButton";
import {FaLink, FaSearch, FaTimes} from "react-icons/fa";
import ModalButton from "../../../components/common/form/ModalButton";
import CommonSelectorModal from "../../../components/common/form/CommonSelectorModal";
import {useSearchCollectorQuery} from "../collector/collector";
import {DataTableColumn} from "../../../components/common/datatable/DataTableTypes";
import {useGetEnumsQuery} from "../../../app/globalApi";
import {FaMagnifyingGlass, FaTrash} from "react-icons/fa6";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isEditable?: boolean;
    isReadOnly?: boolean
}

const CollectorLinkForm: FC<Props> = ({crumbs, title, btnText, isEditable = true, isReadOnly = false}) => {
    const navigate = useNavigate();
    const {collectorId} = useParams();

    const [collectorData, setCollectorData] = useState<any>({
        collector: "",
        linkType: "",
        url: ""
    });
    const [selectedCollector, setSelectedCollector] = useState<any>(null);

    const {data: enums} = useGetEnumsQuery("");
    const {data: collectorLinkData} = useGetCollectorLinkByIdQuery(collectorId, {skip: !collectorId});
    const [createCollectorLink, createCollectorLinkApiResponse] = useCreateCollectorLinkMutation();
    const [updateCollectorLink, updateCollectorLinkApiResponse] = useUpdateCollectorLinkMutation();
    const [selectedRow, setSelectedRow] = useState<any>({});
    const [showCollectorModal, setShowCollectorModal] = useState<boolean>(false);
    const linkTypeOptions = createOptionListForSelectTag(enums?.["CollectorLinkType"], "value", "constant");
    const [deleteApi, {isLoading}] = useDeleteCollectorLinkMutation();

    useEffect(() => {
        if (collectorLinkData && enums) {
            const {id, type, url, collector} = collectorLinkData;
            const matchedLinkType = linkTypeOptions.find((opt: any) => opt.value === type); // 🔥 match from options!

            setCollectorData({
                id,
                collector: collector?.name || "",
                linkType: matchedLinkType, // ✅ set the full object
                url: url || ""
            });

            setSelectedRow({
                id: collector?.id
            });
        }
    }, [collectorLinkData, enums]);


    const handleFieldChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: any }) => {
        const {name, value} = "target" in e ? e.target : e;
        setCollectorData((prev: any) => ({...prev, [name]: value}));
    };

    const handleClearCollector = () => {
        setSelectedCollector(null);
        setCollectorData((prev: any) => ({...prev, collector: ""}));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const payload = {
            type: collectorData.linkType?.value,
            url: collectorData.url,
            collector: {id: selectedRow?.id}
        };


        const submitAction = async () => {
            if (btnText === "Create") {
                return await createCollectorLink(payload)
            } else {
                return await updateCollectorLink({id: collectorId, requestBody: payload}).unwrap()
            }
        }

        await handleRTKQuery(submitAction, (response: any) => {
            if (response?.success || response?.data?.success) {
                setCollectorData({});
                navigate(AppRoutes.CollectorLink.path);
            }
        });
    };

    const columns: DataTableColumn[] = [
        {
            label: "Name", accessor: "name", filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''}/>
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }],
        },
        {label: "State", accessor: "state.stateAbbreviation"},
        {label: "County", accessor: "countyName"}

    ];

    const handleOnClick = (e: any, name: string) => {
        e.preventDefault();
        setCollectorData({
            ...collectorData,
            collector: selectedRow.name
        })
        setShowCollectorModal(false)
    }

    const handleDelete = async () => {
        if (collectorId) {
            await deleteApi(collectorId).unwrap();
        }
        navigate(AppRoutes.CollectorLink.path)
    };
    return (
        <div className="px-4 py-2">
            <div className="justify-between sm:block md:flex lg:flex">
                <Breadcrumb crumbs={crumbs}/>

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    {
                        hasPermission(["COLLINK-D"]) && collectorId &&
                        <ModalButton
                            triggerButton={
                                <IconButton
                                    icon={<FaTrash/>}
                                    title={"Delete Collector Link"}
                                    hasBackground={true}
                                    className="mr-2"
                                />
                            }
                            modal={
                                <ConfirmationModal
                                    show={true}
                                    setShow={() => {
                                    }}
                                    title={"Delete Collector Link"}
                                    bodyMessage={"Are you sure you want to delete this Collector Link?"}
                                    onSubmit={() => handleDelete()}
                                    isLoading={isLoading}
                                />
                            }
                        />
                    }
                    {
                        hasPermission(["AUDIT-V"]) && collectorId &&
                        <AuditableInfoButton obj={collectorLinkData}/>}
                </div>
            </div>

            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>

                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <div className="grid grid-cols-12">
                                <InputField
                                    name="collector"
                                    label="Collector"
                                    onChange={handleFieldChange}
                                    value={collectorData.collector}
                                    placeholder="Enter Collector..."
                                    disabled={isReadOnly}
                                    icon={<IconButton icon={<FaLink onClick={() => {
                                        if (hasPermission(["COLL-R"]) && selectedRow?.id) {
                                            window.open(`/collectors/${selectedRow.id}`, '_blank');
                                        }
                                    }}/>
                                    }
                                                      className={`text-sm ${collectorData?.collector ? 'text-highlight-4' : 'text-surface-4 cursor-default'
                                                      }`}

                                    />}
                                    onClick={() => {
                                        setShowCollectorModal(true);
                                    }}
                                    className="cursor-pointer"
                                    inputIcon={
                                        collectorData.collector ? (
                                            <FaTimes
                                                className="fa cursor-pointer text-highlight-0 absolute right-5 top-6"
                                                title="Clear"
                                                onClick={handleClearCollector}
                                            />
                                        ) : (
                                            <FaSearch
                                                className="fa cursor-pointer text-highlight-0 absolute right-5 top-6"
                                                title="show Modal"
                                                onClick={() => {
                                                    if (!isReadOnly) {
                                                        setShowCollectorModal(true);
                                                    }

                                                }}
                                            />
                                        )
                                    }
                                />
                                <SelectField
                                    label="Link Type"
                                    value={collectorData.linkType}
                                    placeholder="Select Link Type..."
                                    options={linkTypeOptions}
                                    onChange={(selectedOption) =>
                                        handleFieldChange({
                                            name: "linkType",
                                            value: selectedOption
                                        })
                                    }
                                    required
                                    isDisabled={isReadOnly}
                                />


                                <InputField
                                    name="url"
                                    label="URL"
                                    value={collectorData.url}
                                    onChange={handleFieldChange}
                                    placeholder="Enter URL..."
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>

                            {
                                !isReadOnly &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            type: "cancel",
                                                            onClick: () => navigate(AppRoutes.CollectorLink.path),
                                                        },
                                                        buttonType: "button",
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: createCollectorLinkApiResponse.isLoading || updateCollectorLinkApiResponse.isLoading,

                                                        },
                                                        buttonType: "button",
                                                    }
                                                ]}
                                                align="center"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }

                        </>
                    }
                    />


                </div>
            </div>
            {showCollectorModal &&
                <CommonSelectorModal
                    title="Select Collector"
                    columns={columns}
                    data={useSearchCollectorQuery}
                    show={showCollectorModal}
                    onSubmit={handleOnClick}
                    customerFilter={{
                        sort: "name",
                        sortOrder: "ASCENDING"
                    }}
                    setShow={setShowCollectorModal}
                    setSelectedRow={setSelectedRow}
                />
            }
        </div>
    );
};

export default CollectorLinkForm;
